import { makeObservable, observable, computed, runInAction, reaction, action } from 'mobx';
import { debounce } from 'lodash';
import { Disposer } from '../../custom_shared/misc';
import { IInputsStoreBase } from '../../custom_shared/types';
import { InputBaseModel } from '../models';
import { Utils } from '../../custom_shared/misc';
import { InstanceUtils } from '../misc';
import { FormStore } from '../../custom_shared/stores';
import WorksheetsStore from './WorksheetsStore';
import WorksheetsChangesStore from './WorksheetsChangesStore';

export default class InputsStore extends Disposer implements IInputsStoreBase {
    highlightedInputGuid: string | null = null;

    highlightedInputTimeout: ReturnType<typeof setTimeout> | null = null;

    private debouncedInputChangeRecorders = new Map<string, ReturnType<typeof debounce>>();

    constructor(
        private readonly worksheetsStore: WorksheetsStore,
        private readonly worksheetsChangesStore: WorksheetsChangesStore,
        private readonly formStore: FormStore
    ) {
        super();

        makeObservable(this, {
            highlightedInputGuid: observable,
            inputs: computed,
            selectedWorksheetInputsWithSource: computed,
            inputsLoading: computed,
            handleInputModification: action,
            applyInputValueToAllEntryCodesInBalance: action
        });

        this.reactions.push(
            reaction(
                () => this.selectedWorksheetInputsWithSource,
                inputsWithSource => {
                    inputsWithSource.forEach(input => input.loadAndSyncSourceOptions());
                }
            )
        );
    }

    setHighlightedInputGuid(highlightedInputGuid: string | null, timeout: number = 0) {
        if (this.highlightedInputTimeout) {
            clearTimeout(this.highlightedInputTimeout);
        }

        this.highlightedInputTimeout = setTimeout(() => {
            runInAction(() => {
                this.highlightedInputGuid = highlightedInputGuid;
            });
        }, timeout);
    }

    get inputs() {
        return this.worksheetsStore.worksheets.flatMap(worksheet => worksheet.inputs);
    }

    get selectedWorksheetInputsWithSource() {
        return this.worksheetsStore.selectedWorksheet ? this.worksheetsStore.selectedWorksheet.inputsWithSource : [];
    }

    get inputsLoading() {
        return this.selectedWorksheetInputsWithSource.some(input => input.sourceProvider.loading);
    }

    handleInputModification(input: InputBaseModel, value: unknown) {
        input.setValue(value);
        input.invalidateDependentInputsSources();

        const valueSerialized = Utils.serializeForComparison(input.dto.value);
        const initialValueSerialized = Utils.serializeForComparison(input.dto.initialValue);
        input.setIsModified(valueSerialized !== initialValueSerialized);

        this.recordInputChangeDebounced(input);
    }

    applyInputValueToAllEntryCodesInBalance(input: InputBaseModel) {
        const entryCodeNode = input.node;

        if (!InstanceUtils.isEntryCodeNode(entryCodeNode)) {
            return;
        }

        const balanceNode = entryCodeNode.parentNode;

        if (!balanceNode || !InstanceUtils.isBalanceInformationNode(balanceNode)) {
            return;
        }

        for (const sibling of balanceNode.entryCodeNodes) {
            if (sibling === entryCodeNode) {
                continue;
            }

            const targetInput = sibling.getInputById(input.id);

            if (!targetInput) {
                continue;
            }

            if (targetInput.isReadOnly || sibling.worksheet.isLocked) {
                continue;
            }

            this.handleInputModification(targetInput, input.value);
            targetInput.validate();
        }

        const worksheet = entryCodeNode.worksheet;
        worksheet.setHasChangedData(true);

        if (worksheet.hasValidationError) {
            this.formStore.validateForms([worksheet.id]).then(([validation]) => {
                if (validation?.success) {
                    worksheet.setHasValidationError(false);
                }
            });
        }
    }

    async flushPendingInputChanges() {
        // Drop debounce delay: run every pending sync right now so the backend stays in sync.
        const pendingRecorders = Array.from(this.debouncedInputChangeRecorders.values());

        if (!pendingRecorders.length) {
            return;
        }

        const flushPromises = pendingRecorders.map(async recorder => {
            const result = recorder.flush();

            if (result instanceof Promise) {
                await result;
            }
        });

        await Promise.all(flushPromises);
    }

    dispose() {
        this.clearDebouncedInputChangeRecorders();
        this.disposeReactions();
    }

    private recordInputChangeDebounced(input: InputBaseModel) {
        // Debounce the API call to prevent multiple calls on input change
        let debouncedFunction = this.debouncedInputChangeRecorders.get(input.guid);

        if (!debouncedFunction) {
            debouncedFunction = debounce(async () => {
                await this.worksheetsChangesStore.recordPendingInputChange(input);
            }, 300);

            this.debouncedInputChangeRecorders.set(input.guid, debouncedFunction);
        }

        debouncedFunction();
    }

    private clearDebouncedInputChangeRecorders() {
        const recorders = this.debouncedInputChangeRecorders;
        recorders.forEach(recorder => recorder.cancel());
        recorders.clear();
    }
}
